import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bank,
  Sparkle,
  Gear,
  ListChecks,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatEUR, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PERMISSIONS } from "../lib/permissions";
import {
  REMESA_ESTADO_LABEL,
  REMESA_ESTADO_STYLE,
  MESES_ES,
} from "../lib/remesa";
import ConfigForm from "../components/ConfigForm";

const TABS = [
  { id: "remesas", label: "Remesas", icon: ListChecks },
  {
    id: "config",
    label: "Configuración acreedor",
    icon: Gear,
    perm: PERMISSIONS.MANAGE_ORGANIZATION_CONFIG,
  },
];

const todayYM = () => {
  const d = new Date();
  return { anio: d.getFullYear(), mes: d.getMonth() + 1 };
};

export default function Remesas() {
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.MANAGE_REMESAS);
  const visibleTabs = TABS.filter((t) => !t.perm || can(t.perm));

  const [tab, setTab] = useState("remesas");
  const [remesas, setRemesas] = useState([]);
  const [modalGen, setModalGen] = useState(null);
  const [loading, setLoading] = useState(true);

  const remesasSorted = useMemo(
    () =>
      [...remesas].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      ),
    [remesas],
  );

  const fetchRemesas = useCallback(async () => {
    try {
      setLoading(true);

      const r = await api.get("/remesas");

      setRemesas(r.data || []);
    } catch (err) {
      toast.error("No se pudieron cargar las remesas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRemesas();
  }, []);

  const openGenerar = async () => {
    const { anio, mes } = todayYM();
    const modal = { anio, mes, preview: null, loading: true };
    setModalGen(modal);
    await loadPreview(anio, mes, modal);
  };

  const loadPreview = async (anio, mes, base) => {
    try {
      const res = await api.get(`/remesas/preview-mes/${anio}/${mes}`);
      setModalGen({
        ...(base || modalGen),
        anio,
        mes,
        preview: res.data,
        loading: false,
      });
    } catch (err) {
      setModalGen({
        ...(base || modalGen),
        anio,
        mes,
        preview: null,
        loading: false,
      });
      toast.error(
        "No se pudo cargar la preview: " +
          (err?.response?.data?.detail || err.message),
      );
    }
  };

  const confirmGenerar = async () => {
    if (!modalGen?.preview || modalGen.preview.incluibles === 0) return;
    try {
      const res = await api.post("/remesas/generar", {
        anio: modalGen.anio,
        mes: modalGen.mes,
      });
      toast.success(
        `Remesa generada con ${res.data.num_recibos} recibos (${formatEUR(res.data.total)})`,
      );
      setModalGen(null);
      fetchRemesas();
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="remesas-page">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Centro financiero
          </div>
          <h1 className="font-display text-4xl font-bold text-med-text">
            Remesas
          </h1>
          {canManage && (
            <p className="text-med-muted mt-1">
              Genera lotes de cobro, exporta SEPA y mantén la configuración del
              acreedor.
            </p>
          )}
        </div>
        {tab === "remesas" && canManage && (
          <div className="flex flex-wrap gap-2" data-testid="remesas-actions">
            <button
              onClick={openGenerar}
              data-testid="generar-mes-btn"
              className="inline-flex items-center gap-2 bg-med-secondary hover:bg-med-secondary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold active:scale-[0.98]"
            >
              <Sparkle size={18} weight="duotone" />
              Generar remesa del mes
            </button>
          </div>
        )}
      </header>

      {/* Tabs */}
      <nav
        className="flex gap-1 border-b border-med-border mb-6"
        data-testid="remesas-tabs"
      >
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                active
                  ? "border-med-primary text-med-primary"
                  : "border-transparent text-med-muted hover:text-med-text"
              }`}
            >
              <Icon size={16} weight={active ? "fill" : "duotone"} />
              {t.label}
            </button>
          );
        })}
      </nav>

      {/* Tab content */}
      {tab === "remesas" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {loading ? (
              <div className="md:col-span-2 xl:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-med-surface border border-med-border rounded-2xl p-5 animate-pulse"
                  >
                    <div className="w-10 h-10 rounded-xl bg-med-surface2 mb-4" />

                    <div className="h-6 bg-med-surface2 rounded mb-2" />

                    <div className="h-4 bg-med-surface2 rounded w-1/3 mb-6" />

                    <div className="grid grid-cols-2 gap-3">
                      <div className="h-12 bg-med-surface2 rounded" />
                      <div className="h-12 bg-med-surface2 rounded" />
                    </div>

                    <div className="h-10 bg-med-surface2 rounded-xl mt-5" />
                  </div>
                ))}
              </div>
            ) : remesas.length === 0 ? (
              <div className="md:col-span-2 xl:col-span-3 bg-med-surface border border-med-border rounded-2xl p-16 text-center text-med-muted">
                Aún no hay remesas.{" "}
                {canManage && 'Pulsa "Generar remesa del mes" para empezar.'}
              </div>
            ) : (
              remesasSorted.map((r) => <RemesaCard key={r.id} r={r} />)
            )}
          </div>
        </>
      )}

      {tab === "config" && can(PERMISSIONS.MANAGE_ORGANIZATION_CONFIG) && (
        <div data-testid="config-tab-content">
          <ConfigForm />
        </div>
      )}

      {modalGen && (
        <GenerarMesModal
          state={modalGen}
          onChangeYM={(anio, mes) => loadPreview(anio, mes)}
          onClose={() => setModalGen(null)}
          onConfirm={confirmGenerar}
        />
      )}
    </div>
  );
}

const RemesaCard = React.memo(function RemesaCard({ r }) {
  const style = REMESA_ESTADO_STYLE[r.estado] || REMESA_ESTADO_STYLE.borrador;
  return (
    <div
      className="bg-med-surface border border-med-border rounded-2xl p-5 hover:shadow-md transition-shadow"
      data-testid={`remesa-card-${r.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-med-secondary/10 text-med-secondary grid place-items-center">
          <Bank size={20} weight="duotone" />
        </div>
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${style}`}
          data-testid={`estado-${r.id}`}
        >
          {REMESA_ESTADO_LABEL[r.estado] || r.estado}
        </span>
      </div>
      <h3 className="font-display text-xl font-bold text-med-text">
        {r.nombre}
      </h3>
      <p className="text-xs text-med-muted mt-0.5">{formatDate(r.fecha)}</p>
      {r.descripcion && (
        <p className="text-sm text-med-muted mt-2 line-clamp-2">
          {r.descripcion}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 mt-5 pt-4 border-t border-med-border/60">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-med-muted">
            Recibos
          </div>
          <div className="font-semibold text-med-text">{r.num_recibos}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.12em] text-med-muted">
            Total
          </div>
          <div className="font-display text-lg font-bold text-med-primary">
            {formatEUR(r.total)}
          </div>
        </div>
      </div>
      <Link
        to={`/remesas/${r.id}`}
        data-testid={`open-remesa-${r.id}`}
        className="mt-4 inline-flex w-full items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-med-surface2 hover:bg-med-border text-med-text text-sm font-semibold transition-colors"
      >
        Abrir detalle
        <CaretRight size={14} weight="bold" />
      </Link>
    </div>
  );
});

function GenerarMesModal({ state, onChangeYM, onClose, onConfirm }) {
  const { anio, mes, preview, loading } = state;
  const incluibles = preview?.incluibles ?? 0;
  const total = preview?.total ?? 0;
  const excluidos = preview?.excluidos || {};
  const totalExcluidos = Object.values(excluidos).reduce((a, b) => a + b, 0);

  return (
    <div
      className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
      onClick={onClose}
      data-testid="generar-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-med-surface rounded-2xl w-full max-w-lg overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-med-border">
          <h3 className="font-display text-xl font-bold">
            Generar remesa del mes
          </h3>
          <p className="text-sm text-med-muted">
            Agrupa recibos pendientes con mandato domiciliado válido del mes
            seleccionado.
          </p>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                Mes
              </label>
              <select
                value={mes}
                onChange={(e) => onChangeYM(anio, Number(e.target.value))}
                data-testid="generar-mes-select"
                className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
              >
                {MESES_ES.map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                Año
              </label>
              <input
                type="number"
                value={anio}
                onChange={(e) => onChangeYM(Number(e.target.value), mes)}
                data-testid="generar-anio-input"
                className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
              />
            </div>
          </div>

          <div
            className="bg-med-surface2 rounded-xl p-4"
            data-testid="generar-preview"
          >
            {loading ? (
              <div className="text-sm text-med-muted">Calculando…</div>
            ) : (
              <>
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.12em] text-med-muted">
                      Recibos incluibles
                    </div>
                    <div
                      className="font-display text-3xl font-bold text-med-primary"
                      data-testid="preview-incluibles"
                    >
                      {incluibles}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-[0.12em] text-med-muted">
                      Total
                    </div>
                    <div
                      className="font-display text-2xl font-bold text-med-text"
                      data-testid="preview-total"
                    >
                      {formatEUR(total)}
                    </div>
                  </div>
                </div>
                {totalExcluidos > 0 && (
                  <div className="pt-3 border-t border-med-border space-y-1.5 text-xs text-med-muted">
                    <div className="font-semibold uppercase tracking-wide mb-1">
                      {totalExcluidos} excluido{totalExcluidos === 1 ? "" : "s"}
                    </div>
                    {excluidos.pagados > 0 && (
                      <div data-testid="excl-pagados">
                        · {excluidos.pagados} ya pagados
                      </div>
                    )}
                    {excluidos.ya_remesados > 0 && (
                      <div data-testid="excl-remesados">
                        · {excluidos.ya_remesados} en otra remesa
                      </div>
                    )}
                    {excluidos.anulados > 0 && (
                      <div data-testid="excl-anulados">
                        · {excluidos.anulados} anulados
                      </div>
                    )}
                    {excluidos.sin_mandato_valido > 0 && (
                      <div data-testid="excl-sin-mandato">
                        · {excluidos.sin_mandato_valido} sin mandato domiciliado
                        válido
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-med-border flex justify-end gap-3 bg-med-surface">
          <button
            onClick={onClose}
            data-testid="generar-cancel-btn"
            className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text hover:border-med-primary hover:text-med-primary font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || incluibles === 0}
            data-testid="generar-confirm-btn"
            className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold"
          >
            Generar borrador
          </button>
        </div>
      </motion.div>
    </div>
  );
}
